import moment from 'moment';
import React, { useState } from 'react';
import {
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useTranslation } from 'react-i18next';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useCalendar } from '../../CalendarContext/CalendarContext';
import FinalDateDetails from './FinalDateDetails';
import { getOrderPage, getOrderPages } from '../../../utils/AccountUtils';
import { useAuth } from '../../../session/InternalAuthProvider';
import { useLanguage } from '../../../common/GeneralUtils';
import { RecurringScheduleConfig } from '../../../common/ui/RecurringScheduleConfig';
import {
  AccountData,
  CustomDetail,
  CustomTicketsData,
  EntityType,
  EventService,
  OrderPageData,
  OrderPageTypes,
} from '../../../api';
import { UsersMultiSelect } from '../../components/UsersMultiSelect';
import { getEffectivePageLocationId } from '../../../common/filter/locationGrouping';
import { LocationPicker } from '../../../experienceSettings/sections/settingsSection/LocationPicker';
import { WorkshopCircle } from '../../../common/ui/WorkshopCircle';
import { useEvents } from '../../swr/useEvents';
import { DialogState, RecurrentUpdateDialog } from '../../components/RecurrentUpdateDialog';
import { useAssignmentConflictHandler } from '../../../common/apiNotifications/responseHandlers/useAssignmentConflictHandler';
import { useApiResponseHandlerContext } from '../../../common/apiNotifications/useApiResponseHandlerContext';
import { ApiResponseHandlerType } from '../../../common/apiNotifications/ApiResponseHandlerContext';
import { FormDatePicker } from '../../../experienceSettings/formComponents/FormDatePicker';
import { FormTextField } from '../../../experienceSettings/formComponents/FormTextField';
import { FormCheckbox } from '../../../experienceSettings/formComponents/FormCheckBox';
import { MultiTimePicker } from './MultiTimePicker';

const DATE_FORMAT = 'YYYY-MM-DD';

type EditEventFormValues = {
  date: string;
  times: number[];
  tickets: number;
  custom_tickets: CustomTicketsData[];
  employee_ids: string[];
  custom_detail_enabled: boolean;
  hidden_from_customers: boolean;
  location_id?: string;
  repeating?: { enabled: boolean; days: number[]; end_date: string };
  max_voucher_percentage?: number;
};

export const EditEventDialogContent = () => {
  const {
    experienceAvailabilityDrawerState: { orderPageId, event: stateEvent },
    closeExperienceAvailabilityDrawer,
    users,
    startDate: calendarStartDate,
    endDate: calendarEndDate,
  } = useCalendar();

  const {
    addItems: addEvents,
    updateItem: updateEvent,
    reFetch,
  } = useEvents({
    startDate: calendarStartDate,
    endDate: calendarEndDate,
  });

  const { t } = useTranslation();
  const { executeWithConflictCheck } = useAssignmentConflictHandler();
  const { showResponseNotification } = useApiResponseHandlerContext();

  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;

  const language = useLanguage();
  const eventOrderPages = getOrderPages(account).filter(
    (x: OrderPageData) => x.order_page_type === OrderPageTypes.EVENT,
  );
  let orderPage = eventOrderPages[0];
  if (orderPageId) {
    orderPage = getOrderPage(account, orderPageId);
  }

  const isEditEvent = !!stateEvent;
  const [selectedOrderPage, setSelectedOrderPage] = useState(orderPage || eventOrderPages[0]);
  // currently half hacked only to support properly one setting (english for example).
  // if present, it is shown, if not, it is not.
  const englishCustomDetail = selectedOrderPage.custom_details?.find((x: any) => x.name === 'english');
  const customPricedTicketsDetails = selectedOrderPage.objects.filter((x: any) => x.is_custom_price_ticket);

  const [loading, setLoading] = useState(false);
  const [eventUpdateDialogStatus, setEventUpdateDialogStatus] = useState(DialogState.CLOSED);

  const isRecurrentEvent = !!stateEvent?.recurrent_event_id;
  const isAutogeneratedEvent = !!stateEvent?.generated_from_working_days && !isRecurrentEvent;
  const isSeriesEvent = isRecurrentEvent || isAutogeneratedEvent;

  const initialValues: EditEventFormValues = isEditEvent
    ? {
        date: moment(stateEvent.date).format(DATE_FORMAT),
        times: [stateEvent.time ?? 60 * 60 * 9],
        tickets: stateEvent.tickets ?? 0,
        custom_tickets: stateEvent.custom_tickets ?? [],
        employee_ids: stateEvent.employee_ids ?? [],
        custom_detail_enabled: !!stateEvent.custom_details?.[0],
        hidden_from_customers: stateEvent.hidden_from_customers ?? false,
        location_id: stateEvent.location_id ?? orderPage?.location_id,
        max_voucher_percentage: stateEvent.max_voucher_percentage,
      }
    : {
        date: moment().format(DATE_FORMAT),
        times: [orderPage.default_time || 60 * 60 * 9],
        tickets: orderPage.default_tickets_amount || 20,
        custom_tickets: [],
        employee_ids: [],
        custom_detail_enabled: false,
        hidden_from_customers: false,
        location_id: orderPage?.location_id,
        repeating: { enabled: false, days: [0, 1, 2, 3, 4], end_date: '' },
      };

  const validationSchema = yup.object().shape({
    date: yup.string().required(),
    times: yup.array().of(yup.number().min(0).required()).min(1).required(),
    tickets: yup.number().min(0).required(),
    max_voucher_percentage: yup
      .number()
      .min(0)
      .max(100)
      .nullable()
      .optional()
      .transform((value) => (Number.isNaN(value) ? null : value)),
  });

  const onSubmit = async (values: EditEventFormValues) => {
    // @ts-ignore
    const customDetails: CustomDetail[] =
      englishCustomDetail && values.custom_detail_enabled
        ? [{ show_in_widget: true, label: englishCustomDetail.label }]
        : [];

    if (isEditEvent) {
      if (isSeriesEvent) {
        setEventUpdateDialogStatus(DialogState.UPDATE);
        return;
      }

      setLoading(true);
      const editedEvent = await executeWithConflictCheck(
        () =>
          EventService.editEvent({
            event_id: stateEvent.id,
            order_page_id: selectedOrderPage.id,
            date: values.date,
            time: values.times[0],
            tickets: values.tickets,
            custom_tickets: values.custom_tickets,
            employee_ids: values.employee_ids,
            // @ts-ignore
            custom_details: customDetails,
            hidden_from_customers: values.hidden_from_customers,
            location_id: values.location_id,
            max_voucher_percentage: values.max_voucher_percentage,
          }),
        t('calendar.edit_event.success'),
        () => setLoading(false),
      );

      if (updateEvent) {
        await updateEvent(editedEvent);
      }
      closeExperienceAvailabilityDrawer();
    } else {
      setLoading(true);
      // @ts-ignore
      const createdEvents = await EventService.createEvent({
        account_id: account.id,
        // @ts-ignore
        experience_id: authState.account.experiences[0].id,
        order_page_id: selectedOrderPage.id,
        date: values.date,
        times: values.times,
        tickets: values.tickets,
        custom_tickets: values.custom_tickets,
        repeating: values.repeating?.enabled ? values.repeating : undefined,
        hidden_from_customers: values.hidden_from_customers,
        location_id: values.location_id,
        max_voucher_percentage: values.max_voucher_percentage,
      });
      if (addEvents) {
        addEvents(createdEvents);
      }
      setLoading(false);
      closeExperienceAvailabilityDrawer();
    }
  };

  return (
    <Formik enableReinitialize initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit}>
      {({ values, setFieldValue, submitForm, errors, submitCount }) => {
        const editEvent = async () => {
          setLoading(true);
          // @ts-ignore
          const customDetails: CustomDetail[] =
            englishCustomDetail && values.custom_detail_enabled
              ? [{ show_in_widget: true, label: englishCustomDetail.label }]
              : [];

          const editedEvent = await executeWithConflictCheck(
            () =>
              EventService.editEvent({
                event_id: stateEvent!.id,
                order_page_id: selectedOrderPage.id,
                date: values.date,
                time: values.times[0],
                tickets: values.tickets,
                custom_tickets: values.custom_tickets,
                employee_ids: values.employee_ids,
                // hack to make english checkbox for yaar
                // @ts-ignore
                custom_details: customDetails,
                hidden_from_customers: values.hidden_from_customers,
                location_id: values.location_id,
                max_voucher_percentage: values.max_voucher_percentage,
              }),
            t('calendar.edit_event.success'),
            () => setLoading(false),
          );

          if (updateEvent) {
            await updateEvent(editedEvent);
          }
          closeExperienceAvailabilityDrawer();
        };

        const editAutogeneratedForward = async () => {
          setLoading(true);
          // @ts-ignore
          const customDetails: CustomDetail[] =
            englishCustomDetail && values.custom_detail_enabled
              ? [{ show_in_widget: true, label: englishCustomDetail.label }]
              : [];

          try {
            const response = await executeWithConflictCheck(
              () =>
                EventService.editEventAutogeneratedForward({
                  event_id: stateEvent!.id,
                  order_page_id: selectedOrderPage.id,
                  date: values.date,
                  time: values.times[0],
                  tickets: values.tickets,
                  custom_tickets: values.custom_tickets,
                  employee_ids: values.employee_ids,
                  // @ts-ignore
                  custom_details: customDetails,
                  hidden_from_customers: values.hidden_from_customers,
                  location_id: values.location_id,
                  max_voucher_percentage: values.max_voucher_percentage,
                }),
              '',
              () => setLoading(false),
              true,
            );
            const skipped = response.skipped_exception_count ?? 0;
            showResponseNotification(
              skipped > 0
                ? t('eventSchedule.forwardEdit.successWithSkipped', {
                    count: response.updated_count,
                    skipped,
                  })
                : t('eventSchedule.forwardEdit.success', { count: response.updated_count }),
              ApiResponseHandlerType.SUCCESS,
            );
            await reFetch();
            closeExperienceAvailabilityDrawer();
          } catch {
            // error toast already surfaced by executeWithConflictCheck
          }
        };

        const editRepeatingEvent = async () => {
          setLoading(true);
          // @ts-ignore
          const customDetails: CustomDetail[] =
            englishCustomDetail && values.custom_detail_enabled
              ? [{ show_in_widget: true, label: englishCustomDetail.label }]
              : [];

          await EventService.editEventRecurrent({
            recurrent_event_id: stateEvent!.recurrent_event_id!,
            order_page_id: selectedOrderPage.id,
            // @ts-ignore
            date: values.date,
            time: values.times[0],
            tickets: values.tickets,
            custom_tickets: values.custom_tickets,
            employee_ids: values.employee_ids,
            // hack to make english checkbox for yaar
            // @ts-ignore
            custom_details: customDetails,
            hidden_from_customers: values.hidden_from_customers,
            location_id: values.location_id,
            max_voucher_percentage: values.max_voucher_percentage,
          });
          await reFetch();
          setLoading(false);
          closeExperienceAvailabilityDrawer();
        };

        return (
          <Grid container flexDirection="column" sx={{ height: '100%' }}>
            <Grid
              container
              flexDirection="column"
              flexWrap="nowrap"
              sx={{
                flex: 1,
                overflowY: 'auto',
                gap: 1.5,
                pt: { xs: '12px', md: '18px' },
                pb: 2,
                paddingInline: { xs: '12px', md: '24px' },
              }}>
              <FormControl>
                <Select
                  value={selectedOrderPage.id}
                  renderValue={() => (
                    <Grid container alignItems="center" gap={1}>
                      <WorkshopCircle account={account} orderPageId={selectedOrderPage.id} language={language} />
                      <Grid item>
                        {(selectedOrderPage.label[language]?.length ?? 0) > 50
                          ? `${selectedOrderPage.label[language]!.substring(0, 50)}...`
                          : selectedOrderPage.label[language]}
                      </Grid>
                    </Grid>
                  )}
                  onChange={(event) => {
                    const newOrderPage = eventOrderPages.find((x: any) => x.id === event.target.value);
                    if (!newOrderPage) {
                      return;
                    }
                    setSelectedOrderPage(newOrderPage);
                    if (!isEditEvent) {
                      void setFieldValue('tickets', newOrderPage.default_tickets_amount || 20);
                      void setFieldValue('times', [newOrderPage.default_time || 60 * 60 * 9]);
                      void setFieldValue('custom_detail_enabled', false);
                      void setFieldValue('custom_tickets', []);
                    }
                    void setFieldValue('location_id', newOrderPage?.location_id);
                  }}>
                  {eventOrderPages.map((x: any) => (
                    <MenuItem key={`eventOrderPages-${x.id}`} value={x.id}>
                      <Grid container alignItems="center" gap={1}>
                        <WorkshopCircle account={account} orderPageId={x.id} language={language} />
                        {x.label[language]}
                      </Grid>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Grid container flexDirection="column" gap={1.5}>
                <FormDatePicker
                  fieldName="date"
                  label={t('calendar.edit_event.date')}
                  isFieldUpdate={isEditEvent}
                  disablePast={false}
                  minDate={moment().subtract(1, 'month')}
                />
                <Grid container flexDirection="column" gap={0.5}>
                  <Typography variant="caption" color="text.secondary">
                    {t('calendar.edit_event.start_time')}
                  </Typography>
                  <MultiTimePicker
                    times={values.times}
                    onChange={(newTimes) => setFieldValue('times', newTimes)}
                    minTime={moment().set({ hour: 6, minute: 0, second: 0, millisecond: 0 })}
                    isEditEvent={isEditEvent}
                  />
                </Grid>
                {[...values.times]
                  .sort((a, b) => a - b)
                  .map((secs) => (
                    <FinalDateDetails
                      key={secs}
                      duration={selectedOrderPage.duration}
                      time={moment().startOf('day').add(secs, 'seconds')}
                      account={account}
                    />
                  ))}
              </Grid>

              <Grid container flexDirection="column" gap={1.5}>
                <FormTextField
                  fieldName="tickets"
                  label={t('calendar.edit_event.tickets_number')}
                  type="number"
                  min={0}
                  isFieldUpdate={isEditEvent}
                />
                <LocationPicker
                  key={selectedOrderPage.id}
                  onSelected={(newLocationId) => setFieldValue('location_id', newLocationId)}
                  initialLocationId={values.location_id}
                  withoutManageLink
                />
              </Grid>

              {customPricedTicketsDetails.length > 0 && (
                <Grid container flexDirection="column" gap={1.5}>
                  {customPricedTicketsDetails.map((customPricedTicketsDetail: any) => (
                    <Grid container flexWrap="nowrap" alignItems="center" gap={1} key={customPricedTicketsDetail.name}>
                      <Grid item xs={6}>
                        <TextField
                          label={customPricedTicketsDetail.label[language]}
                          type="number"
                          InputProps={{ inputProps: { min: 0 } }}
                          value={
                            values.custom_tickets.find((x) => x.name === customPricedTicketsDetail.name)?.tickets || 0
                          }
                          onChange={(e) => {
                            const existing = values.custom_tickets.find(
                              (x) => x.name === customPricedTicketsDetail.name,
                            );
                            void setFieldValue('custom_tickets', [
                              ...values.custom_tickets.filter((x) => x.name !== customPricedTicketsDetail.name),
                              { ...existing, name: customPricedTicketsDetail.name, tickets: +e.target.value },
                            ]);
                          }}
                        />
                      </Grid>
                      <Grid item>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={
                                values.custom_tickets.find((x) => x.name === customPricedTicketsDetail.name)
                                  ?.hidden_from_customers || false
                              }
                              onChange={() => {
                                const existing = values.custom_tickets.find(
                                  (x) => x.name === customPricedTicketsDetail.name,
                                );
                                void setFieldValue('custom_tickets', [
                                  ...values.custom_tickets.filter((x) => x.name !== customPricedTicketsDetail.name),
                                  {
                                    ...existing,
                                    name: customPricedTicketsDetail.name,
                                    tickets: existing?.tickets || 0,
                                    hidden_from_customers: !existing?.hidden_from_customers,
                                  },
                                ]);
                              }}
                            />
                          }
                          label={t('calendar.edit_event.hiddenFromCustomers')}
                        />
                      </Grid>
                    </Grid>
                  ))}
                  <Divider />
                </Grid>
              )}

              {isEditEvent && (
                <Grid container flexDirection="column" gap={1.5}>
                  <UsersMultiSelect
                    users={users}
                    selectedUsersIds={values.employee_ids}
                    setSelectedUsersIds={(ids) => setFieldValue('employee_ids', ids)}
                    locationId={getEffectivePageLocationId(values.location_id, account)}
                  />
                  {englishCustomDetail && (
                    <FormCheckbox
                      fieldName="custom_detail_enabled"
                      label={englishCustomDetail.label?.[language]}
                      isFieldUpdate={isEditEvent}
                    />
                  )}
                </Grid>
              )}

              <Divider />

              <Grid container flexDirection="column" gap={1.5}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                  {t('calendar.edit_event.advanced_settings')}
                </Typography>

                {!isEditEvent && (
                  <Grid>
                    <RecurringScheduleConfig
                      enabled={!!values.repeating?.enabled}
                      onEnabledChange={(checked) => {
                        void setFieldValue('repeating.enabled', checked);
                      }}
                      startDate={moment(values.date)}
                      onChange={(val) => {
                        if (val) {
                          void setFieldValue('repeating.days', val.days);
                          void setFieldValue('repeating.end_date', val.end_date);
                        }
                      }}
                      label={t('calendar.edit_event.repeating')}
                      activityLabelSingular={account.activity_label_singular?.[language]}
                      activityLabelPlural={account.activity_label_plural?.[language]}
                    />
                    {values.repeating?.enabled &&
                      values.times.length > 1 &&
                      values.repeating.end_date &&
                      (() => {
                        const current = moment(values.date);
                        const end = moment(values.repeating.end_date);
                        let occurrences = 0;
                        while (current.isSameOrBefore(end, 'day')) {
                          if ((values.repeating.days ?? []).includes(current.day())) occurrences++;
                          current.add(1, 'day');
                        }
                        return occurrences > 0 ? (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {t('calendar.edit_event.times_total_preview', {
                              times: values.times.length,
                              occurrences,
                              total: values.times.length * occurrences,
                            })}
                          </Typography>
                        ) : null;
                      })()}
                  </Grid>
                )}

                <Grid container alignItems="center" gap={1}>
                  <Switch
                    checked={values.hidden_from_customers}
                    onChange={(e) => setFieldValue('hidden_from_customers', e.target.checked)}
                  />
                  <Typography variant="body2">{t('calendar.edit_event.hiddenEventFromCustomers')}</Typography>
                </Grid>

                <Grid container flexDirection="column" gap={0.5}>
                  <Grid container alignItems="center" gap={1}>
                    <TextField
                      type="number"
                      size="small"
                      value={values.max_voucher_percentage ?? ''}
                      onChange={(e) => {
                        const val = e.target.value === '' ? undefined : Math.min(100, Math.max(0, +e.target.value));
                        void setFieldValue('max_voucher_percentage', val);
                      }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">%</InputAdornment>,
                        inputProps: { min: 0, max: 100 },
                      }}
                      sx={{ width: 100 }}
                    />
                    <Typography variant="body2">{t('calendar.edit_event.max_voucher_percentage')}</Typography>
                  </Grid>
                  {values.max_voucher_percentage != null ? (
                    <Typography variant="caption" color="text.secondary">
                      {t('calendar.edit_event.max_voucher_percentage_calc', {
                        count: Math.floor((values.tickets * values.max_voucher_percentage) / 100),
                      })}
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      {t('calendar.edit_event.max_voucher_percentage_hint')}
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Grid>
            <Grid
              container
              justifyContent="flex-end"
              alignItems="center"
              gap={1}
              sx={{
                flexShrink: 0,
                py: 1.5,
                paddingInline: { xs: '12px', md: '24px' },
                borderTop: '1px solid',
                borderColor: 'mb.lineSoft',
              }}>
              {submitCount > 0 && Object.keys(errors).length > 0 && (
                <Typography variant="body2" color="error">
                  {Object.values(errors).flat().join(', ')}
                </Typography>
              )}
              <LoadingButton
                color="primary"
                loading={loading}
                disabled={values.times.length === 0}
                variant="contained"
                onClick={submitForm}
                sx={{ minWidth: 100 }}>
                {t('save_changes')}
              </LoadingButton>
            </Grid>
            <RecurrentUpdateDialog
              dialogState={eventUpdateDialogStatus}
              setDialogStatus={setEventUpdateDialogStatus}
              recurrentId={isRecurrentEvent ? stateEvent?.recurrent_event_id : undefined}
              recurrentEntityType={EntityType.EVENT}
              skipCount={isAutogeneratedEvent}
              // no delete option in this component
              deleteSingle={async () => {}}
              deleteReccurrent={async () => {}}
              updateSingle={editEvent}
              updateRecurrent={isAutogeneratedEvent ? editAutogeneratedForward : editRepeatingEvent}
              texts={{
                update: isAutogeneratedEvent
                  ? {
                      cta: t('eventSchedule.forwardEdit.cta'),
                      title: t('eventSchedule.forwardEdit.title'),
                      singleOption: t('eventSchedule.forwardEdit.singleOption'),
                      recurrentOption: t('eventSchedule.forwardEdit.forwardOption'),
                    }
                  : {
                      cta: t('calendar.edit_event.edit_cta'),
                      title: t('calendar.edit_event.edit_title_questions'),
                      singleOption: t('calendar.edit_event.singleUpdate'),
                      recurrentOption: t('calendar.edit_event.repeatingUpdate'),
                      recurrentCount: t('calendar.edit_event.recurrent_count'),
                    },
                delete: {
                  title: t('calendar.edit_event.deletetitle'),
                  singleOption: t('calendar.edit_event.single'),
                  recurrentOption: t('calendar.edit_event.repeating'),
                  recurrentCount: t('calendar.edit_event.recurrent_count'),
                },
              }}
            />
          </Grid>
        );
      }}
    </Formik>
  );
};
